/* ============================================================
   Happy Toilet — animations.js
   Scroll reveals, counters, parallax, lazy images
   ============================================================ */

'use strict';

/* ── Scroll Reveal (Intersection Observer) ────────────────── */
function initScrollReveal() {
  const els = document.querySelectorAll('[data-reveal], [data-stagger]');
  if (!els.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.12,
    rootMargin: '0px 0px -40px 0px'
  });

  els.forEach(el => observer.observe(el));
}

/* ── Animated Counter ─────────────────────────────────────── */
function animateCounter(el, end, duration = 1800) {
  const start = 0;
  const range = end - start;
  const startTime = performance.now();
  const suffix = el.dataset.suffix || '';
  const prefix = el.dataset.prefix || '';

  function step(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    // Ease out cubic
    const eased = 1 - Math.pow(1 - progress, 3);
    const current = Math.floor(start + range * eased);
    el.textContent = prefix + current.toLocaleString() + suffix;
    if (progress < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

function initCounters() {
  const counters = document.querySelectorAll('[data-counter]');
  if (!counters.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const el = entry.target;
        const end = parseInt(el.dataset.counter, 10);
        if (!isNaN(end)) animateCounter(el, end);
        observer.unobserve(el);
      }
    });
  }, { threshold: 0.5 });

  counters.forEach(el => observer.observe(el));
}

/* ── Scroll Progress Bar ──────────────────────────────────── */
function initScrollProgress() {
  const bar = document.getElementById('ScrollProgress');
  if (!bar) return;

  window.addEventListener('scroll', () => {
    const docH = document.documentElement.scrollHeight - window.innerHeight;
    const pct = docH > 0 ? (window.scrollY / docH) * 100 : 0;
    bar.style.width = pct + '%';
  }, { passive: true });
}

/* ── Lazy Image Fade-in ───────────────────────────────────── */
function initLazyImages() {
  const imgs = document.querySelectorAll('img[loading="lazy"]');

  imgs.forEach(img => {
    if (img.complete) {
      img.classList.add('img-loaded');
    } else {
      img.addEventListener('load', () => img.classList.add('img-loaded'));
    }
  });
}

/* ── Parallax (subtle, hero only) ────────────────────────────*/
function initParallax() {
  const hero = document.querySelector('.hero__media');
  if (!hero || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  window.addEventListener('scroll', () => {
    const offset = window.scrollY * 0.25;
    hero.style.transform = `translateY(${offset}px)`;
  }, { passive: true });
}

/* ── Section heading underline trigger ───────────────────────*/
function initHeadingUnderlines() {
  const headings = document.querySelectorAll('.section-heading');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  headings.forEach(h => observer.observe(h));
}

/* ── Marquee social proof duplication ────────────────────────*/
function initMarquee() {
  document.querySelectorAll('.marquee-inner').forEach(track => {
    const clone = track.cloneNode(true);
    track.parentElement.appendChild(clone);
  });
}

/* ── Before/After Image Slider ───────────────────────────────*/
function initBASliders() {
  document.querySelectorAll('[data-ba-slider]').forEach(slider => {
    const beforeEl = slider.querySelector('.ba-slider__before');
    const handle = slider.querySelector('.ba-slider__handle');
    if (!beforeEl || !handle) return;

    let isDragging = false;
    let autoAnim = null;
    let autoPos = 50;
    let autoDir = 1;

    function setPosition(pct) {
      pct = Math.max(2, Math.min(98, pct));
      autoPos = pct;
      slider.style.setProperty('--ba-pos', pct + '%');
      handle.style.left = pct + '%';
    }

    function getPct(clientX) {
      const rect = slider.getBoundingClientRect();
      return ((clientX - rect.left) / rect.width) * 100;
    }

    function startAuto() {
      if (autoAnim) return;
      autoAnim = setInterval(() => {
        if (isDragging) return;
        autoPos += autoDir * 0.35;
        if (autoPos >= 78) autoDir = -1;
        if (autoPos <= 22) autoDir = 1;
        slider.style.setProperty('--ba-pos', autoPos + '%');
        handle.style.left = autoPos + '%';
      }, 16);
    }

    function stopAuto() {
      clearInterval(autoAnim);
      autoAnim = null;
    }

    // Start at 50/50
    setPosition(50);

    // Mouse events
    slider.addEventListener('mousedown', e => {
      isDragging = true;
      stopAuto();
      setPosition(getPct(e.clientX));
      e.preventDefault();
    });
    window.addEventListener('mousemove', e => {
      if (!isDragging) return;
      setPosition(getPct(e.clientX));
    });
    window.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      setTimeout(startAuto, 1500);
    });

    // Touch events
    slider.addEventListener('touchstart', e => {
      isDragging = true;
      stopAuto();
      setPosition(getPct(e.touches[0].clientX));
    }, { passive: true });
    slider.addEventListener('touchmove', e => {
      if (!isDragging) return;
      setPosition(getPct(e.touches[0].clientX));
    }, { passive: true });
    slider.addEventListener('touchend', () => {
      isDragging = false;
      setTimeout(startAuto, 1500);
    });

    // Only run while visible
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) startAuto();
        else stopAuto();
      });
    }, { threshold: 0.3 });
    observer.observe(slider);
  });
}

/* ── Add reveal attributes to sections automatically ─────────*/
function decorateSections() {
  // Sections that should fade up
  const fadeUpSelectors = [
    '.trust-bar__item',
    '.real-results__benefit-item',
    '.real-results__plumber-card',
    '.real-results__stats',
    '.how-it-works__step',
    '.product-showcase__info',
    '.subscription-value__content',
    '.subscription-value__card',
    '.faq__item',
    '.blog-card',
    '.product-card',
    '.review-card',
    '.product-description-section__feature',
    '.page-content__title',
  ];

  fadeUpSelectors.forEach(sel => {
    document.querySelectorAll(sel).forEach((el, i) => {
      if (!el.hasAttribute('data-reveal')) {
        el.setAttribute('data-reveal', 'up');
        el.style.transitionDelay = (i % 4) * 0.1 + 's';
      }
    });
  });

  // Right reveal only for content col — slider col is static (no grow effect)
  const contentCol = document.querySelector('.real-results__content-col');
  if (contentCol && !contentCol.hasAttribute('data-reveal')) contentCol.setAttribute('data-reveal', 'right');

  // Section headings
  document.querySelectorAll('.section-heading').forEach(el => {
    if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', 'up');
  });
  document.querySelectorAll('.section-subheading').forEach(el => {
    if (!el.hasAttribute('data-reveal')) {
      el.setAttribute('data-reveal', 'up');
      el.style.transitionDelay = '0.1s';
    }
  });

  // Gallery stagger
  document.querySelectorAll('.product-gallery__thumbs, .product-showcase__thumbs').forEach(el => {
    el.setAttribute('data-stagger', '');
  });
}

/* ── Scroll progress bar DOM injection ───────────────────────*/
function injectScrollProgress() {
  if (document.getElementById('ScrollProgress')) return;
  const bar = document.createElement('div');
  bar.id = 'ScrollProgress';
  document.body.prepend(bar);
}

/* ── Hover tilt for product showcase image ───────────────────*/
function initTilt() {
  document.querySelectorAll('.product-showcase__slide img, .hero__image').forEach(el => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.style.transition = 'transform 0.4s ease';
    el.addEventListener('mousemove', (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * -10;
      el.style.transform = `perspective(600px) rotateY(${x}deg) rotateX(${y}deg) scale(1.02)`;
    });
    el.addEventListener('mouseleave', () => {
      el.style.transform = '';
    });
  });
}

/* ── FAQ animation (override hidden pattern) ─────────────────*/
function initFAQAnimations() {
  // The base theme.js already handles open/close toggle.
  // Here we enhance the answer container to use grid animation.
  document.querySelectorAll('.faq__answer').forEach(answer => {
    // Remove hidden attribute default and use grid-template-rows instead
    answer.removeAttribute('hidden');
  });

  // Re-init FAQ to use grid approach
  document.querySelectorAll('.faq__question').forEach(btn => {
    btn.setAttribute('aria-expanded', 'false');
    const answerId = btn.getAttribute('aria-controls');
    const answer = document.getElementById(answerId);
    if (answer) answer.setAttribute('hidden', '');
  });
}

/* ── Init all ─────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  injectScrollProgress();
  decorateSections();
  initBASliders();
  initScrollReveal();
  initCounters();
  initScrollProgress();
  initLazyImages();
  initParallax();
  initHeadingUnderlines();
  initMarquee();
  initTilt();
});

// Re-run reveal after any dynamic content updates
document.addEventListener('cart:updated', () => {
  initScrollReveal();
});
